import React, { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Zap,
    BanknoteArrowUp,
    CircleCheck,
    Loader2,
    Upload,
    CreditCard,
    Image as ImageIcon,
} from "lucide-react";
import { api } from "@/lib/api";
import { ttAddToCart, ttInitiateCheckout, ttCompletePayment } from "@/lib/tiktok";
import { fbAddToCart, fbInitiateCheckout, fbPurchase, fbAdvancedMatch } from "@/lib/meta";
import { CITIES } from "@/constants/brand";
import { useStorefront } from "@/hooks/useStorefront";
import { HotProductBadge } from "@/components/landing/HotProductBadge";
import OrderSuccessModal from "@/components/landing/OrderSuccessModal";
import OnlinePayBanner from "@/components/landing/OnlinePayBanner";

export default function CheckoutSection({ product }) {
    const bundles = product?.bundles || [];
    const [bundleQty, setBundleQty] = useState(bundles[0]?.quantity || 1);
    const [form, setForm] = useState({
        customer_name: "",
        phone: "",
        address: "",
        city: "",
        notes: "",
        email: "",
    });
    const [loading, setLoading] = useState(false);
    const [order, setOrder] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState("cod");
    const [screenshotUrl, setScreenshotUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const { settings } = useStorefront();
    const initiatedRef = useRef(false);

    const selected = useMemo(
        () => bundles.find((b) => b.quantity === bundleQty) || bundles[0],
        [bundleQty, bundles],
    );

    const onlineMethods = { easypaisa: true, jazzcash: true, hbl: true };
    const isOnline = paymentMethod in onlineMethods;
    const onlineEligible =
        isOnline && !!screenshotUrl && settings?.online_payment_enabled;
    const onlineDiscountAmount = onlineEligible
        ? Number(settings?.online_payment_discount || 0)
        : 0;
    const deliveryFee = Number(settings?.delivery_fee || 0);
    const payableAmount =
        Math.max(0, (selected?.price || 0) - onlineDiscountAmount) + deliveryFee;

    const handleChange = (e) => {
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    };

    const handleScreenshotUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!/^image\//.test(file.type)) {
            toast.error("Please upload an image file (JPG, PNG, WEBP).");
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Image is larger than 5 MB.");
            return;
        }
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/payment-screenshots", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setScreenshotUrl(data.url);
            toast.success("Screenshot uploaded. 100 PKR extra discount applied.");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Upload failed. Please try again.",
            );
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!/^(03[0-9]{9})$/.test(form.phone)) {
            toast.error(
                "Please enter a valid Pakistani mobile number (03XXXXXXXXX).",
            );
            return;
        }
        if (!form.customer_name.trim() || !form.address.trim() || !form.city) {
            toast.error("Please fill all required fields.");
            return;
        }
        if (isOnline && !screenshotUrl) {
            toast.error("Please upload your payment screenshot to confirm.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/orders", {
                product_slug: product.slug,
                bundle_quantity: bundleQty,
                ...form,
                email: form.email.trim() || null,
                payment_method: paymentMethod,
                payment_screenshot_url: isOnline ? screenshotUrl : null,
            });
            setOrder(data);
            ttCompletePayment(data, { email: form.email, phone: form.phone });
            fbPurchase(data, { email: form.email, phone: form.phone });
            toast.success("Order placed successfully!");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Could not place the order. Please try again.",
            );
        } finally {
            setLoading(false);
        }
    };

    if (!bundles.length) return null;

    return (
        <section
            data-testid="checkout-section"
            id="checkout"
            className="py-16 sm:py-24 bg-gradient-to-b from-cream to-forest-50 border-t border-forest-100"
        >
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto mb-10">
                    <span className="bg-forest-800 text-gold-400 text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-widest inline-flex items-center gap-1.5 mb-4 border border-gold-500/30">
                        <Zap className="w-3.5 h-3.5" /> Quick 1-Minute Order
                    </span>
                    <h2 className="font-serif text-3xl sm:text-5xl font-bold text-forest-800 leading-tight">
                        Complete your order
                    </h2>
                    <p className="text-forest-900/70 text-sm sm:text-base mt-3">
                        No card. No signup. Pay cash on delivery, or transfer online and save extra.
                    </p>
                </div>

                <div className="bg-white rounded-3xl shadow-2xl border border-forest-100 overflow-hidden">
                    <form
                        onSubmit={handleSubmit}
                        onFocusCapture={(e) => {
                            if (initiatedRef.current || !["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName) || e.target.type === "radio") return;
                            initiatedRef.current = true;
                            ttInitiateCheckout(product, selected);
                            fbInitiateCheckout(product, selected);
                        }}
                        className="p-6 sm:p-10 space-y-10"
                        data-testid="checkout-form"
                    >
                        <div className="space-y-4">
                        <div className="flex items-center justify-between border-b border-forest-100 pb-3">
                            <h3 className="font-serif font-bold text-lg sm:text-xl text-forest-800 flex items-center gap-3">
                                <span className="w-8 h-8 rounded-full bg-forest-800 text-gold-400 text-xs flex items-center justify-center font-sans font-bold">
                                    1
                                </span>
                                Select your bundle
                            </h3>
                            <HotProductBadge productId={product?.id} testid="section-hot-badge" />
                        </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {bundles.map((b) => {
                                    const active = bundleQty === b.quantity;
                                    return (
                                        <label
                                            key={b.quantity}
                                            data-testid={`bundle-${b.quantity}`}
                                            className={`cursor-pointer relative rounded-2xl p-4 flex flex-col justify-between transition-all border-2 ${
                                                active
                                                    ? "border-forest-700 bg-forest-50/60 shadow-md"
                                                    : "border-forest-100 hover:border-forest-400 bg-white"
                                            }`}
                                        >
                                            {b.is_popular && (
                                                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold-500 text-forest-900 text-[10px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wider shadow-md">
                                                    Most Popular
                                                </span>
                                            )}
                                            <input
                                                type="radio"
                                                name="bundle"
                                                value={b.quantity}
                                                checked={active}
                                                onChange={() => {
                                                    setBundleQty(b.quantity);
                                                    ttAddToCart(product, b);
                                                    fbAddToCart(product, b);
                                                }}
                                                className="sr-only"
                                            />
                                            <div className="mt-1">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-[10px] font-bold uppercase tracking-wider text-forest-700">
                                                        {b.label}
                                                    </span>
                                                    <span
                                                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                                            active
                                                                ? "border-forest-700 bg-forest-700 text-gold-400"
                                                                : "border-forest-200"
                                                        }`}
                                                    >
                                                        {active && (
                                                            <CircleCheck className="w-3.5 h-3.5 fill-gold-400 text-forest-700" />
                                                        )}
                                                    </span>
                                                </div>
                                                <div className="font-bold text-forest-900 text-base">
                                                    {b.label}
                                                </div>
                                                {b.bonus && (
                                                    <div className="text-xs text-forest-700 font-medium mt-0.5">
                                                        {b.bonus}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="mt-4 pt-3 border-t border-forest-100 flex items-baseline justify-between">
                                                <span className="text-lg font-bold text-forest-800">
                                                    {b.price.toLocaleString()} PKR
                                                </span>
                                                {b.original_price > b.price && (
                                                    <span className="text-xs text-forest-700/50 line-through">
                                                        {b.original_price.toLocaleString()} PKR
                                                    </span>
                                                )}
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <h3 className="font-serif font-bold text-lg sm:text-xl text-forest-800 flex items-center gap-3 border-b border-forest-100 pb-3">
                                <span className="w-8 h-8 rounded-full bg-forest-800 text-gold-400 text-xs flex items-center justify-center font-sans font-bold">
                                    2
                                </span>
                                Delivery information
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field
                                    label="Full Name"
                                    required
                                    name="customer_name"
                                    testid="input-name"
                                    value={form.customer_name}
                                    onChange={handleChange}
                                    placeholder="e.g. Muhammad Ahmed"
                                />
                                <Field
                                    label="Mobile Number"
                                    required
                                    name="phone"
                                    testid="input-phone"
                                    value={form.phone}
                                    onChange={handleChange}
                                    onBlur={() => fbAdvancedMatch({ phone: form.phone })}
                                    placeholder="03XXXXXXXXX"
                                    hint="Format: 03001234567"
                                    inputMode="numeric"
                                    maxLength={11}
                                />
                                <Field
                                    className="sm:col-span-2"
                                    label="Complete Street Address"
                                    required
                                    name="address"
                                    testid="input-address"
                                    value={form.address}
                                    onChange={handleChange}
                                    placeholder="House No, Street Name, Area / Sector"
                                />
                                <div>
                                    <label className="block text-[11px] font-bold text-forest-800 uppercase tracking-widest mb-1.5">
                                        City *
                                    </label>
                                    <select
                                        required
                                        name="city"
                                        data-testid="input-city"
                                        value={form.city}
                                        onChange={handleChange}
                                        className="w-full bg-forest-50/50 border border-forest-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500 focus:bg-white transition-all"
                                    >
                                        <option value="" disabled>
                                            Select your city
                                        </option>
                                        {CITIES.map((c) => (
                                            <option key={c} value={c}>
                                                {c}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <Field
                                    label="Delivery Notes (Optional)"
                                    name="notes"
                                    testid="input-notes"
                                    value={form.notes}
                                    onChange={handleChange}
                                    placeholder="e.g. Call before delivery"
                                />
                                <Field
                                    label="Email (Optional, for order updates)"
                                    name="email"
                                    testid="input-email"
                                    type="email"
                                    autoComplete="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    onBlur={() => fbAdvancedMatch({ email: form.email, phone: form.phone })}
                                    placeholder="you@example.com"
                                    className="sm:col-span-2"
                                />
                            </div>
                        </div>

                        <div className="space-y-4">
                            <h3 className="font-serif font-bold text-lg sm:text-xl text-forest-800 flex items-center gap-3 border-b border-forest-100 pb-3">
                                <span className="w-8 h-8 rounded-full bg-forest-800 text-gold-400 text-xs flex items-center justify-center font-sans font-bold">
                                    3
                                </span>
                                Payment method
                            </h3>
                            <OnlinePayBanner testid="section-online-pay-banner" />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <PaymentOption
                                    value="cod"
                                    label="Cash on Delivery"
                                    sub="Pay cash at the door"
                                    icon={BanknoteArrowUp}
                                    testid="section-payment-cod"
                                    current={paymentMethod}
                                    onChange={setPaymentMethod}
                                />
                                {settings?.online_payment_enabled && (
                                    <>
                                        <PaymentOption
                                            value="easypaisa"
                                            label="Easypaisa"
                                            sub="Mobile wallet"
                                            icon={CreditCard}
                                            testid="section-payment-easypaisa"
                                            badge={`Save ${settings.online_payment_discount}`}
                                            current={paymentMethod}
                                            onChange={setPaymentMethod}
                                        />
                                        <PaymentOption
                                            value="jazzcash"
                                            label="JazzCash"
                                            sub="Mobile wallet"
                                            icon={CreditCard}
                                            testid="section-payment-jazzcash"
                                            badge={`Save ${settings.online_payment_discount}`}
                                            current={paymentMethod}
                                            onChange={setPaymentMethod}
                                        />
                                        <PaymentOption
                                            value="hbl"
                                            label="HBL Bank Transfer"
                                            sub="IBAN / account transfer"
                                            icon={CreditCard}
                                            testid="section-payment-hbl"
                                            badge={`Save ${settings.online_payment_discount}`}
                                            current={paymentMethod}
                                            onChange={setPaymentMethod}
                                        />
                                    </>
                                )}
                            </div>

                            {isOnline && settings?.payment_methods && (
                                <div
                                    data-testid="section-online-payment-panel"
                                    className="rounded-2xl bg-white border border-forest-100 p-4 space-y-3"
                                >
                                    <PaymentDetails
                                        method={paymentMethod}
                                        details={settings.payment_methods[paymentMethod]}
                                    />

                                    <label className="block">
                                        <span className="block text-[11px] font-bold text-forest-800 uppercase tracking-widest mb-1.5">
                                            Upload payment screenshot *
                                        </span>
                                        <div className="flex items-center gap-3">
                                            <label
                                                htmlFor="section-payment-screenshot-input"
                                                className="inline-flex items-center gap-2 bg-forest-800 hover:bg-forest-900 text-gold-400 text-xs font-bold px-4 py-2.5 rounded-xl cursor-pointer border border-gold-500/40"
                                            >
                                                {uploading ? (
                                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                ) : (
                                                    <Upload className="w-3.5 h-3.5" />
                                                )}
                                                {screenshotUrl ? "Replace screenshot" : "Upload screenshot"}
                                            </label>
                                            <input
                                                id="section-payment-screenshot-input"
                                                data-testid="section-payment-screenshot-input"
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                disabled={uploading}
                                                onChange={handleScreenshotUpload}
                                            />
                                            {screenshotUrl && (
                                                <span className="inline-flex items-center gap-1 text-xs text-forest-700 font-semibold">
                                                    <ImageIcon className="w-3.5 h-3.5" />
                                                    Uploaded
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-[11px] text-forest-700/70 mt-1 block">
                                            JPG/PNG/WEBP, up to 5 MB. Required to confirm payment.
                                        </span>
                                    </label>
                                </div>
                            )}

                            <div className="bg-cream p-5 rounded-2xl border border-forest-100 space-y-2">
                                <SummaryRow
                                    label="Selected Package"
                                    value={selected?.label}
                                />
                                <SummaryRow
                                    label="Subtotal"
                                    value={`${selected?.price?.toLocaleString()} PKR`}
                                />
                                {onlineEligible && (
                                    <SummaryRow
                                        label="Online payment bonus"
                                        value={`−${onlineDiscountAmount.toLocaleString()} PKR`}
                                        valueClass="text-forest-700 font-bold"
                                    />
                                )}
                                <SummaryRow
                                    label="Delivery Fee (Pakistan)"
                                    value={deliveryFee > 0 ? `${deliveryFee.toLocaleString()} PKR` : "FREE"}
                                    valueClass={deliveryFee > 0 ? "" : "text-forest-700 font-bold"}
                                />
                                <div className="border-t border-forest-100 pt-2 flex justify-between items-baseline">
                                    <span className="text-sm font-bold text-forest-900">
                                        Total Payable Amount
                                    </span>
                                    <span
                                        data-testid="order-total"
                                        className="text-2xl font-extrabold text-forest-800 font-serif"
                                    >
                                        {payableAmount.toLocaleString()} PKR
                                    </span>
                                </div>
                            </div>
                        </div>

                        <button
                            type="submit"
                            data-testid="submit-order-btn"
                            disabled={loading}
                            className="w-full bg-forest-800 hover:bg-forest-900 disabled:opacity-70 text-white font-bold text-base sm:text-xl py-4 rounded-2xl shadow-xl transition-all flex items-center justify-center gap-3 border-2 border-gold-500"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin text-gold-400" />
                                    Placing your order...
                                </>
                            ) : (
                                <>
                                    <CircleCheck className="w-6 h-6 text-gold-400 fill-forest-700" />
                                    {paymentMethod === "cod"
                                        ? "CONFIRM CASH ON DELIVERY ORDER"
                                        : "CONFIRM ONLINE ORDER"}
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>

            <OrderSuccessModal order={order} onClose={() => setOrder(null)} />
        </section>
    );
}

function Field({ label, required, hint, className = "", testid, ...props }) {
    return (
        <div className={className}>
            <label className="block text-[11px] font-bold text-forest-800 uppercase tracking-widest mb-1.5">
                {label} {required && "*"}
            </label>
            <input
                data-testid={testid}
                required={required}
                className="w-full bg-forest-50/50 border border-forest-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500 focus:bg-white transition-all"
                {...props}
            />
            {hint && (
                <span className="text-[11px] text-forest-700/70 mt-1 block">
                    {hint}
                </span>
            )}
        </div>
    );
}

function PaymentOption({ value, label, sub, icon: Icon, testid, badge, current, onChange }) {
    const active = current === value;
    return (
        <label
            data-testid={testid}
            className={`cursor-pointer rounded-2xl p-3 border-2 transition-all relative ${active ? "border-forest-700 bg-white shadow" : "border-forest-100 bg-white hover:border-forest-400"}`}
        >
            {badge && (
                <span className="absolute -top-2.5 right-3 bg-gold-500 text-forest-900 text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider shadow">
                    {badge}
                </span>
            )}
            <input
                type="radio"
                name="payment"
                checked={active}
                onChange={() => onChange(value)}
                className="sr-only"
            />
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-forest-800 text-gold-400 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                    <div className="font-bold text-forest-900 text-sm leading-tight truncate">
                        {label}
                    </div>
                    <div className="text-[11px] text-forest-700/80 truncate">
                        {sub}
                    </div>
                </div>
            </div>
        </label>
    );
}

function PaymentDetails({ method, details }) {
    if (!details) return null;
    const copy = (val) => {
        try {
            navigator.clipboard.writeText(val);
            toast.success("Copied");
        } catch {
            /* ignore */
        }
    };
    const row = (k, v) => (
        <button
            type="button"
            key={k}
            onClick={() => copy(v)}
            className="w-full flex items-center justify-between bg-forest-50 hover:bg-forest-100 border border-forest-100 rounded-lg px-3 py-2 text-left transition-colors"
        >
            <span className="text-[10px] font-bold uppercase tracking-widest text-forest-700">
                {k}
            </span>
            <span className="font-mono text-xs text-forest-900 font-bold truncate ml-3">
                {v}
            </span>
        </button>
    );
    return (
        <div
            data-testid={`section-payment-details-${method}`}
            className="bg-forest-900 text-cream rounded-xl p-4 space-y-2 border border-gold-500/30"
        >
            <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-gold-400 uppercase tracking-widest">
                    {details.label || method}
                </span>
                <span className="text-[10px] text-cream/60">Tap to copy</span>
            </div>
            <div className="space-y-1.5">
                {details.title && row("Account Title", details.title)}
                {details.number && row("Account Number", details.number)}
                {details.bank && row("Bank", details.bank)}
                {details.iban && row("IBAN", details.iban)}
            </div>
            <p className="text-[11px] text-cream/70 pt-1">
                Send the exact total shown below, then upload a screenshot of your transfer / wallet payment to confirm.
            </p>
        </div>
    );
}

function SummaryRow({ label, value, valueClass = "" }) {
    return (
        <div className="flex justify-between text-xs text-forest-900/70">
            <span>{label}:</span>
            <span className={`font-semibold text-forest-900 ${valueClass}`}>
                {value}
            </span>
        </div>
    );
}
